import type { EsConditionConfig } from "@/components/ConditionPageLayoutEs";
import {
  BRAIN_IMG,
  ES_REVIEWED_BY,
  ES_LAST_UPDATED,
  esBaseCrumbs,
} from "./esConditionShared";

export const esChronicPain: EsConditionConfig = {
  canonical: "/es/chronic-pain-coral-springs-fl",
  badge: "Terapia con ketamina IV",
  title: "Tratamiento del dolor crónico con ketamina",
  subtitle:
    "La infusión de ketamina IV puede ofrecer alivio para el dolor crónico cuando los tratamientos convencionales no son suficientes, en Coral Springs, FL.",
  introHeading:
    "Alivio del dolor crónico con tratamiento de ketamina IV en Coral Springs, FL",
  introText: [
    "El dolor crónico puede ser agotador, afectando cada aspecto de la vida diaria. Cuando los analgésicos convencionales, incluidos los opioides, no ofrecen suficiente alivio o causan efectos secundarios intolerables, la infusión de ketamina IV puede ayudar.",
    "Ofrecemos tratamiento con ketamina IV como una opción complementaria basada en evidencia para adultos con dolor crónico en Coral Springs, FL.",
  ],
  whatIsTitle: "¿Qué es la ketamina para el dolor crónico?",
  whatIsText: [
    "La ketamina ha mostrado eficacia para ciertos tipos de dolor crónico, particularmente el dolor neuropático y el dolor relacionado con la sensibilización central.",
    "La infusión de ketamina IV en dosis baja actúa sobre los receptores NMDA del cerebro y la médula espinal, que desempeñan un papel clave en la percepción y la amplificación del dolor crónico.",
  ],
  howItHelpsTitle: "¿Cómo ayuda la ketamina con el dolor crónico?",
  howItHelpsText: [
    "El dolor crónico a menudo implica una sensibilización del sistema nervioso, en la que los receptores NMDA se sobreactivan y amplifican las señales de dolor. La ketamina bloquea estos receptores, lo que puede ayudar a 'reiniciar' la respuesta del sistema nervioso y reducir la intensidad del dolor.",
    "A diferencia de los opioides, que conllevan riesgos significativos de dependencia, la ketamina IV ofrece un mecanismo de acción diferente para el manejo del dolor crónico.",
  ],
  treatmentExperienceTitle: "¿Cómo es una sesión de tratamiento con ketamina?",
  treatmentExperienceText: [
    "Cada sesión se realiza en nuestro centro en Coral Springs, FL, con supervisión médica continua. La infusión de dosis baja se administra lentamente en un entorno seguro.",
    "Nuestro equipo clínico está presente durante toda la sesión para garantizar su seguridad y comodidad, y para ajustar el tratamiento según sea necesario.",
  ],
  benefitsTitle:
    "Beneficios del tratamiento con ketamina para el dolor crónico",
  benefits: [
    {
      title: "Alivio del dolor",
      desc: "La ketamina puede reducir la intensidad del dolor crónico, particularmente el dolor neuropático.",
    },
    {
      title: "Alternativa a los opioides",
      desc: "Ofrece un mecanismo de acción diferente con un perfil de efectos secundarios distinto al de los opioides.",
    },
    {
      title: "Se dirige a la sensibilización central",
      desc: "Modula los receptores NMDA implicados en la amplificación del dolor crónico.",
    },
    {
      title: "Cuidado ambulatorio",
      desc: "El tratamiento se realiza en consultorio, sin hospitalización.",
    },
  ],
  imageUrl: BRAIN_IMG,
  imageAlt: "Tratamiento del dolor crónico en Coral Springs, FL",
  activeConditionHref: "/es/chronic-pain-coral-springs-fl",
  seoTitle:
    "Ketamina para el dolor crónico en Coral Springs, FL | Neuro Wellness",
  seoDescription:
    "Infusión de ketamina IV para el dolor crónico y neuropático en Coral Springs, FL. Una alternativa basada en evidencia cuando los tratamientos convencionales no son suficientes.",
  keywords:
    "ketamina dolor crónico Coral Springs, tratamiento dolor crónico Coral Springs FL, dolor neuropático ketamina",
  reviewedBy: ES_REVIEWED_BY,
  lastUpdated: ES_LAST_UPDATED,
  breadcrumbs: esBaseCrumbs(
    "Dolor crónico",
    "/es/chronic-pain-coral-springs-fl",
  ),
};

export const esCrps: EsConditionConfig = {
  canonical: "/es/crps-treatment-coral-springs-fl",
  badge: "Terapia con ketamina IV",
  title: "Tratamiento del SDRC (CRPS) con ketamina",
  subtitle:
    "La infusión de ketamina IV es una opción basada en evidencia para el síndrome de dolor regional complejo en Coral Springs, FL.",
  introHeading:
    "Alivio del SDRC con tratamiento de ketamina IV en Coral Springs, FL",
  introText: [
    "El síndrome de dolor regional complejo (SDRC, o CRPS por sus siglas en inglés) es una afección de dolor crónico que generalmente afecta una extremidad después de una lesión o cirugía. El dolor es desproporcionado respecto a la gravedad de la lesión inicial y puede empeorar con el tiempo.",
    "Ofrecemos infusión de ketamina IV como una opción complementaria basada en evidencia para adultos con SDRC en Coral Springs, FL.",
  ],
  whatIsTitle: "¿Qué es la ketamina para el SDRC?",
  whatIsText: [
    "La ketamina ha mostrado eficacia para el SDRC al actuar sobre los receptores NMDA implicados en la sensibilización central que caracteriza esta afección.",
    "La infusión de ketamina IV en dosis baja puede ayudar a reducir la intensidad del dolor y mejorar la función en algunos pacientes con SDRC.",
  ],
  howItHelpsTitle: "¿Cómo ayuda la ketamina con el SDRC?",
  howItHelpsText: [
    "El SDRC implica una sobreactivación de los receptores NMDA en el sistema nervioso, que amplifica las señales de dolor. La ketamina bloquea estos receptores, lo que puede ayudar a reducir la sensibilización y la intensidad del dolor.",
    "El tratamiento se coordina como parte de un plan integral que puede incluir fisioterapia y manejo de medicación.",
  ],
  treatmentExperienceTitle: "¿Cómo es una sesión de tratamiento con ketamina?",
  treatmentExperienceText: [
    "Cada sesión se realiza en nuestro centro en Coral Springs, FL, con supervisión médica continua. La infusión de dosis baja se administra lentamente en un entorno seguro y controlado.",
    "Nuestro equipo clínico está presente durante toda la sesión para garantizar su seguridad y comodidad.",
  ],
  benefitsTitle: "Beneficios del tratamiento con ketamina para el SDRC",
  benefits: [
    {
      title: "Reducción del dolor",
      desc: "La ketamina puede ayudar a reducir la intensidad del dolor asociado con el SDRC.",
    },
    {
      title: "Se dirige a la sensibilización central",
      desc: "Modula los receptores NMDA implicados en la amplificación del dolor del SDRC.",
    },
    {
      title: "Enfoque complementario",
      desc: "Se puede combinar con fisioterapia y manejo de medicación.",
    },
    {
      title: "Cuidado ambulatorio",
      desc: "El tratamiento se realiza en consultorio, sin hospitalización.",
    },
  ],
  imageUrl: BRAIN_IMG,
  imageAlt: "Tratamiento del SDRC en Coral Springs, FL",
  activeConditionHref: "/es/crps-treatment-coral-springs-fl",
  seoTitle:
    "Ketamina para el SDRC (CRPS) en Coral Springs, FL | Neuro Wellness",
  seoDescription:
    "Infusión de ketamina IV para el síndrome de dolor regional complejo en Coral Springs, FL. Cuidado complementario basado en evidencia.",
  keywords:
    "ketamina SDRC Coral Springs, tratamiento CRPS Coral Springs FL, síndrome de dolor regional complejo ketamina",
  reviewedBy: ES_REVIEWED_BY,
  lastUpdated: ES_LAST_UPDATED,
  breadcrumbs: esBaseCrumbs(
    "SDRC (CRPS)",
    "/es/crps-treatment-coral-springs-fl",
  ),
};

export const esFibromyalgia: EsConditionConfig = {
  canonical: "/es/fibromyalgia-treatment-coral-springs-fl",
  badge: "Terapia con ketamina IV",
  title: "Tratamiento de la fibromialgia con ketamina",
  subtitle:
    "La infusión de ketamina IV puede ofrecer alivio para el dolor y la fatiga de la fibromialgia en Coral Springs, FL.",
  introHeading:
    "Alivio de la fibromialgia con tratamiento de ketamina IV en Coral Springs, FL",
  introText: [
    "La fibromialgia causa dolor musculoesquelético generalizado, fatiga y problemas de sueño. Cuando los tratamientos convencionales no ofrecen suficiente alivio, la infusión de ketamina IV puede ayudar.",
    "Ofrecemos tratamiento con ketamina IV como una opción complementaria basada en evidencia para adultos con fibromialgia en Coral Springs, FL.",
  ],
  whatIsTitle: "¿Qué es la ketamina para la fibromialgia?",
  whatIsText: [
    "La ketamina ha mostrado potencial para reducir el dolor y la fatiga en algunos pacientes con fibromialgia.",
    "La infusión de ketamina IV en dosis baja actúa sobre los receptores NMDA y el glutamato, que se cree están implicados en la sensibilización central que caracteriza la fibromialgia.",
  ],
  howItHelpsTitle: "¿Cómo ayuda la ketamina con la fibromialgia?",
  howItHelpsText: [
    "La fibromialgia implica una sensibilización del sistema nervioso central, en la que el cerebro amplifica las señales de dolor. La ketamina modula la actividad del glutamato y los receptores NMDA, lo que puede ayudar a reducir esta amplificación.",
    "El tratamiento se coordina como parte de un plan integral que puede incluir medicación, psicoterapia y cambios en el estilo de vida.",
  ],
  treatmentExperienceTitle: "¿Cómo es una sesión de tratamiento con ketamina?",
  treatmentExperienceText: [
    "Cada sesión se realiza en nuestro centro en Coral Springs, FL, con supervisión médica continua. La infusión de dosis baja se administra lentamente en un entorno seguro y controlado.",
    "Nuestro equipo clínico está presente durante toda la sesión para garantizar su seguridad y comodidad.",
  ],
  benefitsTitle: "Beneficios del tratamiento con ketamina para la fibromialgia",
  benefits: [
    {
      title: "Reducción del dolor",
      desc: "La ketamina puede ayudar a reducir el dolor generalizado asociado con la fibromialgia.",
    },
    {
      title: "Se dirige a la sensibilización central",
      desc: "Modula los receptores NMDA implicados en la amplificación del dolor.",
    },
    {
      title: "Enfoque complementario",
      desc: "Se puede combinar con medicación y otros enfoques de tratamiento.",
    },
    {
      title: "Cuidado ambulatorio",
      desc: "El tratamiento se realiza en consultorio, sin hospitalización.",
    },
  ],
  imageUrl: BRAIN_IMG,
  imageAlt: "Tratamiento de la fibromialgia en Coral Springs, FL",
  activeConditionHref: "/es/fibromyalgia-treatment-coral-springs-fl",
  seoTitle:
    "Ketamina para la fibromialgia en Coral Springs, FL | Neuro Wellness",
  seoDescription:
    "Infusión de ketamina IV para el dolor y la fatiga de la fibromialgia en Coral Springs, FL. Cuidado complementario basado en evidencia.",
  keywords:
    "ketamina fibromialgia Coral Springs, tratamiento fibromialgia Coral Springs FL, dolor fibromialgia ketamina",
  reviewedBy: ES_REVIEWED_BY,
  lastUpdated: ES_LAST_UPDATED,
  breadcrumbs: esBaseCrumbs(
    "Fibromialgia",
    "/es/fibromyalgia-treatment-coral-springs-fl",
  ),
};
